import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/SimpleAuthContext';
import { opportunityService } from '../../services/opportunityService';
import './OpportunityList.css';

const OpportunityList = () => {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [opportunities, setOpportunities] = useState([]);
  const [filters, setFilters] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState(null);
  const [error, setError] = useState('');
  const [retry, setRetry] = useState(0);
  const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || '');
  const selectedFilters = {
    opportunity_type: searchParams.get('opportunity_type') || searchParams.get('type') || '',
    location: searchParams.get('location') || '',
    can_apply: searchParams.get('can_apply') === 'true',
    is_featured: (searchParams.get('is_featured') || searchParams.get('featured')) === 'true'
  };
  const query = searchParams.toString();

  useEffect(() => {
    let active = true;
    opportunityService.getOpportunityFilters().then(data => { if (active) setFilters(data); }).catch(() => {});
    return () => { active = false; };
  }, []);

  useEffect(() => {
    let active = true;
    const params = Object.fromEntries(new URLSearchParams(query));
    // Keep existing shared links working, but send the API's canonical keys.
    if (params.featured) { params.is_featured = params.featured; delete params.featured; }
    if (params.type) { params.opportunity_type = params.type; delete params.type; }
    setSearchTerm(params.search || '');
    setLoading(true);
    setError('');
    opportunityService.getOpportunities(params).then(data => {
      if (!active) return;
      setOpportunities(Array.isArray(data) ? data : (data.results || []));
      setPagination({
        count: data.count ?? data.length ?? 0,
        next: data.next,
        previous: data.previous,
        current_page: Math.ceil((params.page || 1)),
        total_pages: Math.ceil(data.count / 12)
      });
    }).catch(() => { if (active) setError('Opportunities could not be loaded. Please try again.'); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [query, retry]);

  const handleSearch = (e) => {
    e.preventDefault();
    updateSearchParams({ ...selectedFilters, search: searchTerm, page: 1 });
  };

  const handleFilterChange = (filterName, value) => {
    const newFilters = { ...selectedFilters, [filterName]: value };
    
    // Update URL params
    const params = { ...newFilters, page: 1 };
    if (searchTerm) params.search = searchTerm;
    
    // Remove empty values
    Object.keys(params).forEach(key => {
      if (!params[key] || params[key] === false) {
        delete params[key];
      }
    });
    
    updateSearchParams(params);
  };

  const updateSearchParams = (params) => {
    const newSearchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value) newSearchParams.set(key, value);
    });
    setSearchParams(newSearchParams);
  };

  const handlePageChange = (page) => {
    const params = Object.fromEntries(searchParams);
    params.page = page;
    updateSearchParams(params);
  };

  const clearFilters = () => {
    setSearchTerm('');
    setSearchParams(new URLSearchParams());
  };

  if (loading && !opportunities.length) {
    return (
      <div className="opportunity-list">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading opportunities...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="opportunity-list">
      <div className="list-header">
        <div className="header-content">
          <Link to="/opportunities" className="browse-back">Back to Opportunity Hub</Link>
          <h1>Browse opportunities</h1>
          <p>Find jobs, internships and training opportunities that suit your next step.</p>
        </div>
        
        {user && (user.is_staff || user.role === 'admin' || user.role === 'manager') && (
          <div className="header-actions">
            <Link to="/opportunities/create" className="btn-primary">
              Create Opportunity
            </Link>
          </div>
        )}
      </div>

      {/* Search and Filters */}
      <div className="search-filters">
        <div className="search-section">
          <form onSubmit={handleSearch} className="search-form">
            <input
              type="text"
              aria-label="Search opportunities"
              placeholder="Search opportunities..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
            <button type="submit" className="search-btn">
              Search
            </button>
          </form>
        </div>

        {filters && (
          <div className="filters-section">
            <div className="filter-group">
              <label htmlFor="opportunity-type">Type</label>
              <select
                id="opportunity-type"
                value={selectedFilters.opportunity_type}
                onChange={(e) => handleFilterChange('opportunity_type', e.target.value)}
                className="filter-select"
              >
                <option value="">All Types</option>
                {filters.opportunity_types.map(type => (
                  <option key={type.value} value={type.value}>
                    {type.label} ({type.count})
                  </option>
                ))}
              </select>
            </div>

            <div className="filter-group">
              <label htmlFor="opportunity-location">Location</label>
              <select
                id="opportunity-location"
                value={selectedFilters.location}
                onChange={(e) => handleFilterChange('location', e.target.value)}
                className="filter-select"
              >
                <option value="">All Locations</option>
                {filters.locations.map(location => (
                  <option key={location} value={location}>
                    {location}
                  </option>
                ))}
              </select>
            </div>

            <div className="filter-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={selectedFilters.can_apply}
                  onChange={(e) => handleFilterChange('can_apply', e.target.checked)}
                />
                Can Apply Now
              </label>
            </div>

            <div className="filter-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={selectedFilters.is_featured}
                  onChange={(e) => handleFilterChange('is_featured', e.target.checked)}
                />
                Featured Only
              </label>
            </div>

            <button onClick={clearFilters} className="clear-filters-btn">
              Clear Filters
            </button>
          </div>
        )}
      </div>

      {/* Results */}
      <div className="results-section">
        {error && <div className="browse-error" role="alert"><p>{error}</p><button className="btn-outline" onClick={() => setRetry(value => value + 1)}>Try again</button></div>}
        {pagination && (
          <div className="results-header">
            <p className="results-count">
              Showing {opportunities.length} of {pagination.count} opportunities
            </p>
          </div>
        )}

        {opportunities.length > 0 ? (
          <>
            <div className="opportunities-grid">
              {opportunities.map((opportunity) => (
                <div key={opportunity.id} className="opportunity-card">
                  <div className="card-header">
                    <div className="opportunity-type">
                      <span className="type-icon">
                        {opportunityService.getTypeIcon(opportunity.opportunity_type)}
                      </span>
                      <span className="type-text">
                        {opportunity.opportunity_type_display}
                      </span>
                    </div>
                    
                    <div className="card-badges">
                      {opportunity.is_featured && (
                        <span className="featured-badge">⭐ Featured</span>
                      )}
                      <span className={`status-badge status-${opportunity.computed_status || opportunity.status}`}>
                        {opportunity.computed_status_display || opportunity.status_display}
                      </span>
                      <span className={`deadline-badge ${opportunityService.getDeadlineUrgency(opportunity.application_deadline)}`}>
                        {opportunityService.formatDeadline(opportunity.application_deadline)}
                      </span>
                    </div>
                  </div>

                  <div className="card-content">
                    <h3>{opportunity.title}</h3>
                    <p className="description">
                      {opportunity.description.length > 120 
                        ? `${opportunity.description.substring(0, 120)}...`
                        : opportunity.description
                      }
                    </p>

                    <div className="opportunity-meta">
                      {opportunity.location && (
                        <span className="meta-item">
                          📍 {opportunity.location}
                        </span>
                      )}
                      <span className="meta-item">
                        👥 {opportunity.available_capacity}/{opportunity.capacity} spots
                      </span>
                      <span className="meta-item">
                        📅 {opportunityService.formatDeadline(opportunity.application_deadline)}
                      </span>
                    </div>

                    <div className="capacity-bar">
                      <div 
                        className="capacity-fill" 
                        style={{ width: `${opportunity.capacity_percentage}%` }}
                      ></div>
                    </div>
                  </div>

                  <div className="card-actions">
                    <Link to={`/opportunities/${opportunity.id}`} className="btn-outline">
                      View Details
                    </Link>
                    {opportunity.can_apply && (
                      <Link to={`/opportunities/${opportunity.id}/apply`} className="btn-primary">
                        Apply Now
                      </Link>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination */}
            {pagination && pagination.total_pages > 1 && (
              <div className="pagination">
                <button
                  onClick={() => handlePageChange(pagination.current_page - 1)}
                  disabled={!pagination.previous}
                  className="pagination-btn"
                >
                  ← Previous
                </button>
                
                <span className="pagination-info">
                  Page {pagination.current_page} of {pagination.total_pages}
                </span>
                
                <button
                  onClick={() => handlePageChange(pagination.current_page + 1)}
                  disabled={!pagination.next}
                  className="pagination-btn"
                >
                  Next →
                </button>
              </div>
            )}
          </>
        ) : !error && !loading ? (
          <div className="empty-state">
            <div className="empty-content">
              <h2>No opportunities found</h2>
              <p>We couldn't find any opportunities matching your criteria.</p>
              <button onClick={clearFilters} className="btn-outline">
                Clear Filters
              </button>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default OpportunityList;
